import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, Trash2, Edit2, Download, Search, Filter, ChevronDown, ChevronUp,
  FileText, DollarSign, Clock, CheckCircle, XCircle, ArrowRight, X, Send, Receipt, Link2,
  Paperclip, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  estimate: 'bg-yellow-500/20 text-yellow-400',
  approved: 'bg-blue-500/20 text-blue-400',
  in_progress: 'bg-purple-500/20 text-purple-400',
  completed: 'bg-green-500/20 text-green-400',
  invoiced: 'bg-orange-500/20 text-orange-400',
  paid: 'bg-electric-teal/20 text-electric-teal',
  cancelled: 'bg-red-500/20 text-red-400'
};

const PAYMENT_COLORS = {
  unpaid: 'bg-red-500/20 text-red-400',
  partial: 'bg-yellow-500/20 text-yellow-400',
  paid: 'bg-green-500/20 text-green-400'
};

const fmt = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : '$0.00');

// ==================== LINE ITEMS EDITOR ====================
const LineItemsEditor = ({ items, onChange, label }) => {
  const addItem = () => onChange([...items, { description: '', quantity: 1, rate: 0, amount: 0 }]);
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: value };
    if (field === 'quantity' || field === 'rate') {
      updated[i].amount = (parseFloat(updated[i].quantity) || 0) * (parseFloat(updated[i].rate) || 0);
    }
    onChange(updated);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-white font-semibold">{label}</Label>
        <Button onClick={addItem} variant="ghost" size="sm" className="text-electric-teal text-xs h-7">
          <Plus className="w-3 h-3 mr-1" /> Add Line
        </Button>
      </div>
      {items.length > 0 && (
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_70px_90px_90px_32px] gap-2 text-white/40 text-xs px-1">
            <span>Description</span><span>Qty</span><span>Rate</span><span>Amount</span><span></span>
          </div>
          {items.map((item, i) => (
            <div key={i} className="grid grid-cols-[1fr_70px_90px_90px_32px] gap-2 items-center">
              <Input value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)}
                className="bg-primary-dark border-white/20 text-white text-sm h-8" placeholder="Description" />
              <Input type="number" value={item.quantity} onChange={(e) => updateItem(i, 'quantity', parseFloat(e.target.value) || 0)}
                className="bg-primary-dark border-white/20 text-white text-sm h-8" />
              <Input type="number" value={item.rate} onChange={(e) => updateItem(i, 'rate', parseFloat(e.target.value) || 0)}
                className="bg-primary-dark border-white/20 text-white text-sm h-8" placeholder="$0" />
              <span className="text-white/60 text-sm text-right pr-1">{fmt(item.amount)}</span>
              <Button onClick={() => removeItem(i)} variant="ghost" size="sm" className="text-red-400 h-8 w-8 p-0">
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
      {items.length === 0 && <p className="text-white/30 text-xs pl-1">No items yet</p>}
    </div>
  );
};

// ==================== JOB DETAIL DIALOG ====================
const JobDetailDialog = ({ job, open, onClose, staff, onSave }) => {
  const [form, setForm] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: 'Bearer ' + token } : {};
  };

  useEffect(() => {
    if (job) {
      setForm({ ...job });
      setAttachments(job.attachments || []);
    } else {
      setForm({
        customer_name: '', customer_email: '', customer_phone: '', customer_type: 'residential',
        address: '', location_area: 'victoria', service_type: '', assigned_estimator_id: '',
        client_booking_date: '', estimated_start_date: '', actual_start_date: '', end_date: '',
        line_items: [], subtotal: 0, tax_rate: 12, tax_amount: 0, estimate_total: 0,
        add_on_items: [], add_on_subtotal: 0, add_on_tax: 0, add_on_total: 0,
        final_bill_amount: 0, payment_status: 'unpaid', amount_paid: 0,
        hours_worked: 0, travel_time_hours: 0, job_status: 'estimate',
        notes: '', permit_number: '', permit_status: 'not_required',
      });
      setAttachments([]);
    }
  }, [job]);

  const recalc = (f) => {
    const sub = (f.line_items || []).reduce((s, i) => s + (i.amount || 0), 0);
    const tax = sub * ((f.tax_rate || 0) / 100);
    const addSub = (f.add_on_items || []).reduce((s, i) => s + (i.amount || 0), 0);
    const addTax = addSub * ((f.tax_rate || 0) / 100);
    return {
      ...f, subtotal: sub, tax_amount: tax, estimate_total: sub + tax,
      add_on_subtotal: addSub, add_on_tax: addTax, add_on_total: addSub + addTax,
      final_bill_amount: sub + tax + addSub + addTax
    };
  };

  const update = (field, value) => setForm(prev => recalc({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.customer_name) { toast.error('Customer name is required'); return; }
    const data = recalc(form);
    onSave(data);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-secondary-dark border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-electric-teal" />
            {job ? `Edit Job ${job.job_number || ''}` : 'New Job'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 mt-2">
          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Customer Name *</Label>
              <Input value={form.customer_name || ''} onChange={(e) => update('customer_name', e.target.value)}
                className="bg-primary-dark border-white/20 text-white h-9" data-testid="job-customer-name" />
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Email</Label>
              <Input value={form.customer_email || ''} onChange={(e) => update('customer_email', e.target.value)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Phone</Label>
              <Input value={form.customer_phone || ''} onChange={(e) => update('customer_phone', e.target.value)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Type</Label>
              <Select value={form.customer_type || 'residential'} onValueChange={(v) => update('customer_type', v)}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="residential">Residential</SelectItem><SelectItem value="commercial">Commercial</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Service</Label>
              <Input value={form.service_type || ''} onChange={(e) => update('service_type', e.target.value)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Estimator</Label>
              <Select value={form.assigned_estimator_id || 'none'} onValueChange={(v) => update('assigned_estimator_id', v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {(staff || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Job Status</Label>
              <Select value={form.job_status || 'estimate'} onValueChange={(v) => update('job_status', v)}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="estimate">Estimate</SelectItem><SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="invoiced">Invoiced</SelectItem><SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Address */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Address</Label>
              <Input value={form.address || ''} onChange={(e) => update('address', e.target.value)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Area</Label>
              <Select value={form.location_area || 'victoria'} onValueChange={(v) => update('location_area', v)}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="victoria">Victoria / Southern</SelectItem><SelectItem value="duncan_north">North of Duncan</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          {/* Dates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Client Booking Date</Label>
              <Input type="date" value={form.client_booking_date || ''} onChange={(e) => update('client_booking_date', e.target.value)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Est. Start Date</Label>
              <Input type="date" value={form.estimated_start_date || ''} onChange={(e) => update('estimated_start_date', e.target.value)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Actual Start</Label>
              <Input type="date" value={form.actual_start_date || ''} onChange={(e) => update('actual_start_date', e.target.value)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">End Date</Label>
              <Input type="date" value={form.end_date || ''} onChange={(e) => update('end_date', e.target.value)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
          </div>
          {/* Line Items */}
          <LineItemsEditor label="Estimate Line Items" items={form.line_items || []}
            onChange={(items) => update('line_items', items)} />
          {/* Tax & Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white/5 p-4 rounded-lg">
            <div>
              <Label className="text-white/50 text-xs">Subtotal</Label>
              <p className="text-white font-semibold">{fmt(form.subtotal)}</p>
            </div>
            <div>
              <Label className="text-white/50 text-xs">Tax Rate (%)</Label>
              <Input type="number" value={form.tax_rate || 12} onChange={(e) => update('tax_rate', parseFloat(e.target.value) || 0)}
                className="bg-primary-dark border-white/20 text-white h-8 w-20" />
            </div>
            <div>
              <Label className="text-white/50 text-xs">Tax Amount</Label>
              <p className="text-white font-semibold">{fmt(form.tax_amount)}</p>
            </div>
            <div>
              <Label className="text-white/50 text-xs">Estimate Total</Label>
              <p className="text-electric-teal font-bold text-lg">{fmt(form.estimate_total)}</p>
            </div>
          </div>
          {/* Add-ons */}
          <LineItemsEditor label="Add-on Items" items={form.add_on_items || []}
            onChange={(items) => update('add_on_items', items)} />
          {form.add_on_items?.length > 0 && (
            <div className="grid grid-cols-3 gap-4 bg-white/5 p-3 rounded-lg">
              <div><Label className="text-white/50 text-xs">Add-on Subtotal</Label><p className="text-white font-semibold">{fmt(form.add_on_subtotal)}</p></div>
              <div><Label className="text-white/50 text-xs">Add-on Tax</Label><p className="text-white font-semibold">{fmt(form.add_on_tax)}</p></div>
              <div><Label className="text-white/50 text-xs">Add-on Total</Label><p className="text-white font-semibold">{fmt(form.add_on_total)}</p></div>
            </div>
          )}
          {/* Final Bill & Payment */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-electric-teal/5 p-4 rounded-lg border border-electric-teal/20">
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Final Bill</Label>
              <p className="text-electric-teal font-bold text-xl">{fmt(form.final_bill_amount)}</p>
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Amount Paid</Label>
              <Input type="number" value={form.amount_paid || 0} onChange={(e) => update('amount_paid', parseFloat(e.target.value) || 0)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Payment Status</Label>
              <Select value={form.payment_status || 'unpaid'} onValueChange={(v) => update('payment_status', v)}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          {/* Time Tracking */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Hours Worked</Label>
              <Input type="number" value={form.hours_worked || 0} onChange={(e) => update('hours_worked', parseFloat(e.target.value) || 0)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Travel Time (hrs)</Label>
              <Input type="number" value={form.travel_time_hours || 0} onChange={(e) => update('travel_time_hours', parseFloat(e.target.value) || 0)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Permit #</Label>
              <Input value={form.permit_number || ''} onChange={(e) => update('permit_number', e.target.value)}
                className="bg-primary-dark border-white/20 text-white h-9" />
            </div>
            <div>
              <Label className="text-white/70 text-xs mb-1 block">Permit Status</Label>
              <Select value={form.permit_status || 'not_required'} onValueChange={(v) => update('permit_status', v)}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_required">Not Required</SelectItem><SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="pulled">Pulled</SelectItem><SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* File Attachments */}
          {job && (
            <div>
              <Label className="text-white/70 text-xs mb-2 block flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> Attachments ({attachments.length})
              </Label>
              <div className="space-y-2 mb-3">
                {attachments.map(att => (
                  <div key={att.id} className="flex items-center justify-between bg-primary-dark rounded-lg px-3 py-2 border border-white/10" data-testid={'attachment-' + att.id}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText className="w-4 h-4 text-electric-teal flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-white text-sm truncate">{att.filename}</p>
                        <p className="text-white/30 text-xs">{(att.size / 1024).toFixed(1)} KB | {att.uploaded_by} | {new Date(att.uploaded_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0 ml-2">
                      <a href={API_BASE + att.url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="border-white/20 text-white/60 hover:bg-white/5 h-7 px-2">
                          <Download className="w-3 h-3" />
                        </Button>
                      </a>
                      <Button size="sm" variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-7 px-2"
                        onClick={async () => {
                          try {
                            await axios.delete(API_BASE + '/api/admin/jobs/' + job.id + '/attachments/' + att.id, { headers: getAuthHeader() });
                            setAttachments(prev => prev.filter(a => a.id !== att.id));
                            toast.success('File deleted');
                          } catch (e) { toast.error('Failed to delete file'); }
                        }}
                        data-testid={'delete-attachment-' + att.id}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <input type="file" ref={fileInputRef} className="hidden" multiple onChange={async (e) => {
                const files = Array.from(e.target.files);
                if (!files.length) return;
                setUploading(true);
                for (const file of files) {
                  try {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('uploaded_by', 'Admin');
                    const res = await axios.post(API_BASE + '/api/admin/jobs/' + job.id + '/attachments', formData, {
                      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                    });
                    setAttachments(prev => [...prev, res.data]);
                    toast.success('Uploaded: ' + file.name);
                  } catch (err) {
                    toast.error('Failed to upload: ' + file.name);
                  }
                }
                setUploading(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }} />
              <Button
                size="sm"
                variant="outline"
                className="border-white/20 text-white/60 hover:bg-white/5"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-attachment-btn"
              >
                <Upload className="w-3 h-3 mr-1" /> {uploading ? 'Uploading...' : 'Upload Files'}
              </Button>
            </div>
          )}
          {/* Notes */}
          <div>
            <Label className="text-white/70 text-xs mb-1 block">Notes</Label>
            <Textarea value={form.notes || ''} onChange={(e) => update('notes', e.target.value)}
              className="bg-primary-dark border-white/20 text-white min-h-[60px]" />
          </div>
          <Button onClick={handleSave} className="w-full bg-electric-teal text-primary-dark font-bold" data-testid="save-job-btn">
            {job ? 'Update Job' : 'Create Job'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ==================== JOBS SPREADSHEET TAB ====================
const JobsTab = ({ staff, onUpdate }) => {
  const [jobs, setJobs] = useState([]);
  const [editingJob, setEditingJob] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const staffMap = {};
  (staff || []).forEach(s => { staffMap[s.id] = s; });

  useEffect(() => { loadJobs(); }, []);

  const loadJobs = async () => {
    try {
      const res = await api.admin.getJobs();
      setJobs(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSave = async (data) => {
    try {
      if (editingJob) {
        await api.admin.updateJob(editingJob.id, data);
        toast.success('Job updated');
      } else {
        await api.admin.createJob(data);
        toast.success('Job created');
      }
      setDialogOpen(false);
      setEditingJob(null);
      loadJobs();
      onUpdate();
    } catch (e) { toast.error('Failed to save job'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await api.admin.deleteJob(id);
      toast.success('Job deleted');
      loadJobs();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleSendEstimate = async (job) => {
    if (!job.customer_email) { toast.error('No email on this job'); return; }
    if (!window.confirm(`Send estimate to ${job.customer_email}?`)) return;
    try {
      await api.admin.sendEstimate(job.id);
      toast.success(`Estimate sent to ${job.customer_email}`);
    } catch (e) { toast.error('Failed to send estimate'); }
  };

  const handleGenerateInvoice = async (job) => {
    if (!window.confirm(`Generate invoice for ${job.job_number}?`)) return;
    try {
      const res = await api.admin.generateInvoice(job.id);
      toast.success(`Invoice generated: $${res.data.final_bill_amount.toFixed(2)}`);
      loadJobs();
    } catch (e) { toast.error('Failed to generate invoice'); }
  };

  const handleSendInvoice = async (job) => {
    if (!job.customer_email) { toast.error('No email on this job'); return; }
    try {
      await api.admin.sendInvoice(job.id);
      toast.success(`Invoice sent to ${job.customer_email}`);
    } catch (e) { toast.error('Failed to send invoice'); }
  };

  const handleSendPortalLink = async (job) => {
    if (!job.customer_email) { toast.error('No email on this job'); return; }
    try {
      await api.admin.sendPortalLink(job.id);
      toast.success(`Portal link sent to ${job.customer_email}`);
    } catch (e) { toast.error('Failed to send portal link'); }
  };

  const handleExport = async () => {
    try {
      const res = await api.admin.exportJobsCsv();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'island_coastal_jobs.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) { toast.error('Export failed'); }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const filtered = jobs
    .filter(j => {
      if (statusFilter !== 'all' && j.job_status !== statusFilter) return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        return (j.customer_name || '').toLowerCase().includes(s) ||
          (j.job_number || '').toLowerCase().includes(s) ||
          (j.service_type || '').toLowerCase().includes(s);
      }
      return true;
    })
    .sort((a, b) => {
      const av = a[sortField] || '';
      const bv = b[sortField] || '';
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const SortIcon = ({ field }) => sortField === field
    ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />)
    : null;

  return (
    <div className="space-y-4" data-testid="jobs-tab">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-secondary-dark border-white/20 text-white pl-9 w-60 h-9" placeholder="Search jobs..." data-testid="job-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-secondary-dark border-white/20 text-white w-36 h-9" data-testid="job-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="estimate">Estimate</SelectItem><SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="invoiced">Invoiced</SelectItem><SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} variant="outline" className="border-white/20 text-white h-9 text-xs" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          <Button onClick={() => { setEditingJob(null); setDialogOpen(true); }}
            className="bg-electric-teal text-primary-dark font-bold h-9" data-testid="new-job-btn">
            <Plus className="w-4 h-4 mr-1" /> New Job
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-secondary-dark rounded-lg border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table w-full" data-testid="jobs-table">
            <thead>
              <tr>
                <th className="text-left text-white/60 text-xs font-medium p-3 cursor-pointer whitespace-nowrap" onClick={() => toggleSort('job_number')}>
                  Job # <SortIcon field="job_number" />
                </th>
                <th className="text-left text-white/60 text-xs font-medium p-3 cursor-pointer whitespace-nowrap" onClick={() => toggleSort('customer_name')}>
                  Customer <SortIcon field="customer_name" />
                </th>
                <th className="text-left text-white/60 text-xs font-medium p-3 whitespace-nowrap">Service</th>
                <th className="text-left text-white/60 text-xs font-medium p-3 whitespace-nowrap">Estimator</th>
                <th className="text-left text-white/60 text-xs font-medium p-3 cursor-pointer whitespace-nowrap" onClick={() => toggleSort('job_status')}>
                  Status <SortIcon field="job_status" />
                </th>
                <th className="text-left text-white/60 text-xs font-medium p-3 whitespace-nowrap">Booking Date</th>
                <th className="text-left text-white/60 text-xs font-medium p-3 whitespace-nowrap">Est. Start</th>
                <th className="text-right text-white/60 text-xs font-medium p-3 cursor-pointer whitespace-nowrap" onClick={() => toggleSort('estimate_total')}>
                  Estimate <SortIcon field="estimate_total" />
                </th>
                <th className="text-right text-white/60 text-xs font-medium p-3 whitespace-nowrap">Final Bill</th>
                <th className="text-left text-white/60 text-xs font-medium p-3 whitespace-nowrap">Payment</th>
                <th className="text-left text-white/60 text-xs font-medium p-3 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((job) => {
                const est = staffMap[job.assigned_estimator_id];
                return (
                  <tr key={job.id} className="hover:bg-white/5 cursor-pointer" onClick={() => { setEditingJob(job); setDialogOpen(true); }}>
                    <td className="p-3 text-electric-teal font-mono text-sm font-semibold">{job.job_number || '-'}</td>
                    <td className="p-3">
                      <p className="text-white text-sm font-medium">{job.customer_name}</p>
                      <p className="text-white/40 text-xs">{job.customer_type}</p>
                    </td>
                    <td className="p-3 text-white/70 text-sm">{job.service_type || '-'}</td>
                    <td className="p-3">
                      {est ? (
                        <span className="flex items-center gap-1.5 text-sm">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: est.color }} />
                          <span className="text-white/70">{est.name.split(' ')[0]}</span>
                        </span>
                      ) : <span className="text-white/30 text-sm">-</span>}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_COLORS[job.job_status] || ''}`}>
                        {(job.job_status || 'estimate').replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-3 text-white/60 text-sm">{job.client_booking_date || 'N/A'}</td>
                    <td className="p-3 text-white/60 text-sm">{job.estimated_start_date || '-'}</td>
                    <td className="p-3 text-right text-white font-semibold text-sm">{fmt(job.estimate_total)}</td>
                    <td className="p-3 text-right text-electric-teal font-semibold text-sm">{fmt(job.final_bill_amount)}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${PAYMENT_COLORS[job.payment_status] || ''}`}>
                        {job.payment_status || 'unpaid'}
                      </span>
                      {(job.attachments?.length > 0) && (
                        <span className="ml-2 inline-flex items-center gap-0.5 text-white/40 text-xs" title={job.attachments.length + ' file(s) attached'}>
                          <Paperclip className="w-3 h-3" />{job.attachments.length}
                        </span>
                      )}
                    </td>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {job.customer_email && (
                          <Button onClick={() => handleSendEstimate(job)} variant="ghost" size="sm" className="text-electric-teal hover:text-electric-teal/80 h-7 w-7 p-0" title="Send Estimate Email">
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {job.customer_email && ['completed', 'invoiced'].includes(job.job_status) && (
                          <Button onClick={() => handleSendInvoice(job)} variant="ghost" size="sm" className="text-green-400 hover:text-green-300 h-7 w-7 p-0" title="Send Invoice">
                            <Receipt className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {job.customer_email && (
                          <Button onClick={() => handleSendPortalLink(job)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 h-7 w-7 p-0" title="Send Portal Link">
                            <Link2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button onClick={() => { setEditingJob(job); setDialogOpen(true); }} variant="ghost" size="sm" className="text-white/60 hover:text-white h-7 w-7 p-0">
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button onClick={() => handleDelete(job.id)} variant="ghost" size="sm" className="text-red-500 hover:text-red-400 h-7 w-7 p-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="text-center py-12 text-white/30">No jobs found. Create your first job or convert a booking.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <JobDetailDialog job={editingJob} open={dialogOpen} onClose={() => { setDialogOpen(false); setEditingJob(null); }}
        staff={staff} onSave={handleSave} />
    </div>
  );
};

export default JobsTab;
